/**
 * 래스터/필드 연산 툴박스.
 *
 * 전부 순수 함수이고 DOM에 의존하지 않는다 — 그래야 웹워커에서도 그대로 돈다.
 * 유일한 예외는 `decodeImage`(ImageBitmap 필요)이며 파일 맨 아래에 몰아뒀다.
 */

import type { Field, Raster } from '../types.ts';

export const clamp = (v: number, lo = 0, hi = 1) => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp((x - e0) / (e1 - e0 || 1e-6));
  return t * t * (3 - 2 * t);
};

export function makeField(width: number, height: number, fill = 0): Field {
  const data = new Float32Array(width * height);
  if (fill !== 0) data.fill(fill);
  return { width, height, data };
}

export function makeRaster(width: number, height: number): Raster {
  return { width, height, data: new Uint8ClampedArray(width * height * 4) };
}

export function cloneField(f: Field): Field {
  return { width: f.width, height: f.height, data: Float32Array.from(f.data) };
}

/** sRGB 0..255 → 선형 0..1. 룩업 테이블로 미리 깔아둔다. */
const SRGB_TO_LINEAR = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    t[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  return t;
})();

export function linearize(v255: number): number {
  return SRGB_TO_LINEAR[v255 | 0]!;
}

export function encodeSrgb(linear: number): number {
  const c = clamp(linear);
  const s = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return s * 255;
}

/** 선형 휘도 필드. Rec.709 가중치. */
export function luminanceField(src: Raster): Field {
  const { width, height, data } = src;
  const out = makeField(width, height);
  for (let i = 0, p = 0; i < out.data.length; i++, p += 4) {
    out.data[i] =
      0.2126 * SRGB_TO_LINEAR[data[p]!]! +
      0.7152 * SRGB_TO_LINEAR[data[p + 1]!]! +
      0.0722 * SRGB_TO_LINEAR[data[p + 2]!]!;
  }
  return out;
}

/** HSV의 S. 0..1. */
export function saturationAt(data: Uint8ClampedArray, p: number): number {
  const r = data[p]!, g = data[p + 1]!, b = data[p + 2]!;
  const max = r > g ? (r > b ? r : b) : g > b ? g : b;
  const min = r < g ? (r < b ? r : b) : g < b ? g : b;
  return max === 0 ? 0 : (max - min) / max;
}

/**
 * 분리형 박스 블러. 러닝 섬이라 반지름과 무관하게 O(n).
 * 가장자리는 클램프(edge extend).
 */
export function boxBlur(field: Field, radius: number): Field {
  if (radius < 1) return cloneField(field);
  const { width: w, height: h, data } = field;
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const r = Math.min(radius | 0, Math.max(w, h));
  const norm = 1 / (2 * r + 1);

  // 가로
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let sum = data[row]! * (r + 1);
    for (let x = 1; x <= r; x++) sum += data[row + Math.min(x, w - 1)]!;
    for (let x = 0; x < w; x++) {
      tmp[row + x] = sum * norm;
      sum += data[row + Math.min(x + r + 1, w - 1)]! - data[row + Math.max(x - r, 0)]!;
    }
  }
  // 세로
  for (let x = 0; x < w; x++) {
    let sum = tmp[x]! * (r + 1);
    for (let y = 1; y <= r; y++) sum += tmp[Math.min(y, h - 1) * w + x]!;
    for (let y = 0; y < h; y++) {
      out[y * w + x] = sum * norm;
      sum += tmp[Math.min(y + r + 1, h - 1) * w + x]! - tmp[Math.max(y - r, 0) * w + x]!;
    }
  }
  return { width: w, height: h, data: out };
}

/** 박스 블러 3회 = 가우시안 근사. sigma를 그대로 받는다. */
export function gaussianBlur(field: Field, sigma: number): Field {
  if (sigma <= 0.3) return cloneField(field);
  // Wells 1986 근사
  const r = Math.max(1, Math.round(sigma * 1.2));
  let f = field;
  for (let i = 0; i < 3; i++) f = boxBlur(f, r);
  return f;
}

/** 두 필드를 픽셀 단위로 합성. */
export function combine(a: Field, b: Field, fn: (x: number, y: number) => number): Field {
  const out = makeField(a.width, a.height);
  for (let i = 0; i < out.data.length; i++) out.data[i] = fn(a.data[i]!, b.data[i]!);
  return out;
}

export function mapField(f: Field, fn: (v: number, i: number) => number): Field {
  const out = makeField(f.width, f.height);
  for (let i = 0; i < out.data.length; i++) out.data[i] = fn(f.data[i]!, i);
  return out;
}

/**
 * 백분위 기준 정규화. 아웃라이어 한 픽셀이 전체 스케일을 망치는 걸 막는다.
 * `weight`가 있으면 그 값이 0.5 넘는 픽셀만 통계에 넣는다(= 마스크 안쪽만).
 */
export function normalizeField(f: Field, loPct = 0.01, hiPct = 0.99, weight?: Field): Field {
  const samples: number[] = [];
  const stride = Math.max(1, Math.floor(f.data.length / 60000));
  for (let i = 0; i < f.data.length; i += stride) {
    if (weight && weight.data[i]! < 0.5) continue;
    samples.push(f.data[i]!);
  }
  if (samples.length < 8) return cloneField(f);
  samples.sort((a, b) => a - b);
  const lo = samples[Math.floor(samples.length * loPct)]!;
  const hi = samples[Math.min(samples.length - 1, Math.floor(samples.length * hiPct))]!;
  const span = hi - lo || 1e-6;
  return mapField(f, (v) => clamp((v - lo) / span));
}

/** 3x3 소벨. 반환값은 정규화되지 않은 기울기. */
export function sobel(f: Field): { gx: Field; gy: Field } {
  const { width: w, height: h, data } = f;
  const gx = makeField(w, h);
  const gy = makeField(w, h);
  const at = (x: number, y: number) =>
    data[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))]!;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tl = at(x - 1, y - 1), t = at(x, y - 1), tr = at(x + 1, y - 1);
      const l = at(x - 1, y), r = at(x + 1, y);
      const bl = at(x - 1, y + 1), b = at(x, y + 1), br = at(x + 1, y + 1);
      gx.data[y * w + x] = tl + 2 * l + bl - (tr + 2 * r + br);
      gy.data[y * w + x] = tl + 2 * t + tr - (bl + 2 * b + br);
    }
  }
  return { gx, gy };
}

/** 라플라시안 절댓값 — 디테일 밀도 측정용. */
export function laplacian(f: Field): Field {
  const { width: w, height: h, data } = f;
  const out = makeField(w, h);
  const at = (x: number, y: number) =>
    data[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))]!;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      out.data[y * w + x] = at(x - 1, y) + at(x + 1, y) + at(x, y - 1) + at(x, y + 1) - 4 * at(x, y);
    }
  }
  return out;
}

/** 국소 분산. 평균의 제곱과 제곱의 평균 차이 — 블러 두 번이면 끝난다. */
export function localVariance(f: Field, radius: number): Field {
  const mean = boxBlur(f, radius);
  const sq = mapField(f, (v) => v * v);
  const meanSq = boxBlur(sq, radius);
  return combine(meanSq, mean, (ms, m) => Math.max(0, ms - m * m));
}

/** 쌍선형 리샘플. */
export function resampleField(f: Field, w: number, h: number): Field {
  if (f.width === w && f.height === h) return cloneField(f);
  const out = makeField(w, h);
  const sx = f.width / w;
  const sy = f.height / h;
  for (let y = 0; y < h; y++) {
    const fy = Math.min(f.height - 1, (y + 0.5) * sy - 0.5);
    const y0 = Math.max(0, Math.floor(fy));
    const y1 = Math.min(f.height - 1, y0 + 1);
    const ty = fy - y0;
    for (let x = 0; x < w; x++) {
      const fx = Math.min(f.width - 1, (x + 0.5) * sx - 0.5);
      const x0 = Math.max(0, Math.floor(fx));
      const x1 = Math.min(f.width - 1, x0 + 1);
      const tx = fx - x0;
      const a = lerp(f.data[y0 * f.width + x0]!, f.data[y0 * f.width + x1]!, tx);
      const b = lerp(f.data[y1 * f.width + x0]!, f.data[y1 * f.width + x1]!, tx);
      out.data[y * w + x] = lerp(a, b, ty);
    }
  }
  return out;
}

/** 정규화 좌표(0..1)에서 쌍선형 샘플. 메시 생성기가 격자 위에서 쓴다. */
export function sampleField(f: Field, u: number, v: number): number {
  const fx = clamp(u) * (f.width - 1);
  const fy = clamp(v) * (f.height - 1);
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = Math.min(f.width - 1, x0 + 1), y1 = Math.min(f.height - 1, y0 + 1);
  const tx = fx - x0, ty = fy - y0;
  const a = lerp(f.data[y0 * f.width + x0]!, f.data[y0 * f.width + x1]!, tx);
  const b = lerp(f.data[y1 * f.width + x0]!, f.data[y1 * f.width + x1]!, tx);
  return lerp(a, b, ty);
}

/** 필드를 회색조 래스터로. 인스펙터 미리보기와 텍스처 업로드에 쓴다. */
export function fieldToRaster(f: Field, srgb = false): Raster {
  const out = makeRaster(f.width, f.height);
  for (let i = 0, p = 0; i < f.data.length; i++, p += 4) {
    const v = srgb ? encodeSrgb(f.data[i]!) : clamp(f.data[i]!) * 255;
    out.data[p] = v;
    out.data[p + 1] = v;
    out.data[p + 2] = v;
    out.data[p + 3] = 255;
  }
  return out;
}

// ---------------------------------------------------------------------------
// 여기부터는 브라우저 API가 필요하다.
// ---------------------------------------------------------------------------

/** 긴 변이 `maxSize`를 넘지 않도록 축소하며 디코드한다. */
export async function decodeImage(blob: Blob, maxSize = 1024): Promise<Raster> {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다.');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const img = ctx.getImageData(0, 0, w, h);
  return { width: w, height: h, data: img.data };
}

/** 래스터를 캔버스로. three의 CanvasTexture에 그대로 물린다. */
export function rasterToCanvas(r: Raster): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = r.width;
  canvas.height = r.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다.');
  ctx.putImageData(new ImageData(r.data, r.width, r.height), 0, 0);
  return canvas;
}
